/**
 * CERT PHI Staging & Retention operator panel. Thin UI over the already
 * hardened /api/cert-staging endpoints — backend remains authoritative
 * (AZAI RBAC + recent-MFA + typed phrase all re-checked server-side).
 * No PHI is ever placed in URLs, browser storage or logs: run records
 * carry only artifact names, hashes, counts and timestamps.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, Archive, CheckCircle2, RefreshCw, ShieldAlert, Trash2, XCircle,
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import MfaStepUpDialog from '../../components/auth/MfaStepUpDialog';
import api from '../../services/api';

const STATE_STYLES = {
  STAGED: 'bg-blue-100 text-blue-800',
  CERT_IN_PROGRESS: 'bg-indigo-100 text-indigo-800',
  CERT_COMPLETE: 'bg-emerald-100 text-emerald-800',
  RETENTION_PENDING: 'bg-amber-100 text-amber-800',
  PURGED: 'bg-slate-200 text-slate-600',
};

const fmtBytes = (n) => (n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } };

const scheduledPurgeDate = (run, retention) => {
  if (run.state === 'PURGED') return null;
  const days = run.retention_days ?? retention?.retention_days;
  if (!days || !run.created_at) return null;
  try {
    const d = new Date(run.created_at);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toLocaleDateString();
  } catch { return null; }
};

const isStepUp = (err) => {
  if (err.response?.status !== 403) return false;
  // api.js interceptor flattens `detail` to a string and preserves the
  // structured payload under `detail_object` — consult both shapes.
  const structured = err.response?.data?.detail_object;
  const detail = err.response?.data?.detail;
  const code = (structured && typeof structured === 'object' && structured.error) ||
               (detail && typeof detail === 'object' && detail.error);
  return ['mfa_step_up_required', 'mfa_required'].includes(code);
};

const RetentionCard = ({ retention, onApprove }) => {
  if (!retention) return null;
  const store = retention.store;
  const policy = retention.policy;
  return (
    <div className="space-y-2">
      {retention.approved ? (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 space-y-1"
             data-testid="cert-staging-retention-status">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            <p className="text-xs text-emerald-900">
              <span className="font-semibold">Retention policy approved</span>
              {policy ? ` — v${policy.policy_version} · ${policy.basis} · ${policy.purge_trigger}` : ''}
            </p>
          </div>
          {policy && (
            <p className="text-[11px] text-emerald-800 pl-6" data-testid="cert-staging-policy-detail">
              {policy.retention_days != null
                ? `Fixed retention: ${policy.retention_days} day(s). `
                : 'No fixed-day period (purge governed by trigger). '}
              {policy.citation ? `Citation: ${policy.citation}. ` : ''}
              Approved by {policy.approved_by_email || '—'}.
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 space-y-2"
             data-testid="cert-staging-retention-status">
          <div className="flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
            <p className="text-xs text-red-800">
              <span className="font-bold">NO APPROVED RETENTION POLICY — REAL PHI STAGING BLOCKED.</span>{' '}
              The server refuses staging of operator-supplied member data until an authorized operator
              APPROVES a retention policy. An environment variable never constitutes approval.
              Synthetic generation remains available.
            </p>
          </div>
          <Button size="sm" onClick={onApprove}
                  className="h-7 text-xs bg-slate-900 hover:bg-slate-800"
                  data-testid="cert-staging-approve-policy-btn">
            Approve retention policy…
          </Button>
        </div>
      )}
      {retention.warning && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
             data-testid="cert-staging-legacy-warning">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-[11px] text-amber-900">{retention.warning}</p>
        </div>
      )}
      {retention.approved && (
        <Button size="sm" variant="outline" onClick={onApprove}
                className="h-7 text-xs" data-testid="cert-staging-new-policy-btn">
          Approve a new policy version…
        </Button>
      )}
      {store && (
        <div className={`flex items-center gap-2 rounded-md border px-3 py-2 ${store.healthy
          ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
             data-testid="cert-staging-store-status">
          {store.healthy
            ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            : <ShieldAlert className="h-4 w-4 text-red-600 shrink-0" />}
          <p className={`text-xs ${store.healthy ? 'text-emerald-900' : 'text-red-800'}`}>
            <span className="font-semibold">Persistent encrypted store (AES-256-GCM · GridFS):</span>{' '}
            {store.healthy
              ? 'HEALTHY — staged artifacts survive restarts/redeploys; real PHI never relies on container disk.'
              : `UNAVAILABLE (key ${store.key}, gridfs ${store.gridfs}) — operator-supplied staging FAILS CLOSED until restored.`}
          </p>
        </div>
      )}
    </div>
  );
};

const BASES = [
  ['ORG_RECORDS_RETENTION', 'Organization records-retention policy'],
  ['ODM_OMES_REQUIREMENT', 'ODM/OMES requirement (citation required)'],
  ['CERT_PURPOSE_LIMITATION', 'CERT purpose limitation (minimum-necessary)'],
];
const TRIGGERS = [
  ['FIXED_DAYS', 'Fixed number of days after staging'],
  ['CERT_PURPOSE_CLOSED', 'When the CERT purpose is formally closed'],
  ['MANUAL_ONLY', 'Manual exact-run purge only'],
];

const ApprovePolicyDialog = ({ open, onOpenChange, onApproved, onStepUp }) => {
  const [basis, setBasis] = useState('ORG_RECORDS_RETENTION');
  const [trigger, setTrigger] = useState('FIXED_DAYS');
  const [days, setDays] = useState('2555');
  const [citation, setCitation] = useState('');
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      const payload = {
        basis, purge_trigger: trigger,
        retention_days: trigger === 'FIXED_DAYS' ? parseInt(days, 10) : null,
        citation, evidence,
      };
      const { data } = await api.post('/cert-staging/retention/approve', payload);
      toast.success(`Retention policy v${data.policy.policy_version} approved`);
      onApproved();
      onOpenChange(false);
    } catch (e) {
      if (e.response?.status === 403 && onStepUp) { onStepUp(() => submit()); return; }
      const d = e.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Approval refused by the server.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-lg" data-testid="cert-staging-approve-dialog">
        <DialogHeader>
          <DialogTitle>Approve CERT PHI retention policy</DialogTitle>
          <DialogDescription className="text-xs">
            Immutable, versioned and MFA-gated. Approving grants NO CERT-mode, transmission or
            credential authority — those remain independent controls.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <div>
            <label className="font-semibold text-slate-700">Policy basis</label>
            <select value={basis} onChange={(e) => setBasis(e.target.value)}
                    className="mt-1 w-full border rounded px-2 py-1.5"
                    data-testid="cert-staging-policy-basis">
              {BASES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="font-semibold text-slate-700">Purge trigger</label>
            <select value={trigger} onChange={(e) => setTrigger(e.target.value)}
                    className="mt-1 w-full border rounded px-2 py-1.5"
                    data-testid="cert-staging-policy-trigger">
              {TRIGGERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {trigger === 'FIXED_DAYS' && (
            <div>
              <label className="font-semibold text-slate-700">Retention days (&gt; 0)</label>
              <Input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)}
                     className="mt-1" data-testid="cert-staging-policy-days" />
            </div>
          )}
          <div>
            <label className="font-semibold text-slate-700">
              Authoritative citation {basis === 'ODM_OMES_REQUIREMENT' ? '(required)' : '(recommended)'}
            </label>
            <Input value={citation} onChange={(e) => setCitation(e.target.value)}
                   placeholder="e.g. OAC 5160-1-20 §(x) / org records policy REC-###"
                   className="mt-1" data-testid="cert-staging-policy-citation" />
          </div>
          <div>
            <label className="font-semibold text-slate-700">
              Supporting evidence {basis === 'ODM_OMES_REQUIREMENT' ? '(required)' : ''}
            </label>
            <Input value={evidence} onChange={(e) => setEvidence(e.target.value)}
                   placeholder="Where the period (if any) is authoritatively stated"
                   className="mt-1" data-testid="cert-staging-policy-evidence" />
          </div>
          {err && (
            <p className="text-red-600 flex items-start gap-1" data-testid="cert-staging-policy-error">
              <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {err}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="cert-staging-policy-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-slate-900 hover:bg-slate-800"
                  data-testid="cert-staging-policy-submit">Approve policy</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const renderRunRow = (run, retention, onPurge) => {
  const state = run.derived_state || run.state;
  const purgeDue = scheduledPurgeDate(run, retention);
  return (
    <TableRow key={run.id} data-testid={`cert-staging-run-row-${run.id}`}>
      <TableCell className="px-3 py-2 font-mono text-[11px] text-slate-700" title={run.id}>
        {run.id.slice(0, 8)}…
      </TableCell>
      <TableCell className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{fmtDate(run.created_at)}</TableCell>
      <TableCell className="px-3 py-2 text-xs text-slate-600 truncate max-w-[140px]" title={run.operator_email}>
        {run.operator_email || '—'}
      </TableCell>
      <TableCell className="px-3 py-2">
        {run.custom_recipients ? (
          <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`cert-staging-run-class-${run.id}`}>
            OPERATOR CSV
          </Badge>
        ) : (
          <Badge className="bg-slate-100 text-slate-600 text-[10px]" data-testid={`cert-staging-run-class-${run.id}`}>
            SYNTHETIC
          </Badge>
        )}
      </TableCell>
      <TableCell className="px-3 py-2 text-xs text-slate-600 truncate max-w-[150px]"
                 title={run.source_agency_id || ''}
                 data-testid={`cert-staging-run-agency-${run.id}`}>
        {run.source_agency_name || '—'}
      </TableCell>
      <TableCell className="px-3 py-2 text-xs text-slate-700 text-center">{run.inquiry_count ?? '—'}</TableCell>
      <TableCell className="px-3 py-2">
        <Badge className={`${STATE_STYLES[state] || 'bg-slate-100'} text-[10px]`}
               data-testid={`cert-staging-run-state-${run.id}`}>
          {state.replaceAll('_', ' ')}
        </Badge>
      </TableCell>
      <TableCell className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
        {run.state === 'PURGED' ? `purged ${fmtDate(run.purged_at)}` : (purgeDue || '—')}
      </TableCell>
      <TableCell className="px-3 py-2 text-right">
        <Button size="sm" variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50 h-7 text-xs"
                disabled={run.state === 'PURGED'}
                onClick={() => onPurge(run)}
                data-testid={`cert-staging-purge-btn-${run.id}`}>
          <Trash2 className="h-3 w-3 mr-1" /> Purge…
        </Button>
      </TableCell>
    </TableRow>
  );
};

const PurgePlan = ({ preview }) => {
  const deletions = (preview?.plan || []).filter((p) => p.action === 'delete');
  const skipped = (preview?.plan || []).filter((p) => p.action !== 'delete');
  return (
    <div className="space-y-3 text-xs" data-testid="cert-staging-purge-plan">
      <div>
        <p className="font-semibold text-red-800 mb-1">
          PHI-bearing artifacts that WILL BE DELETED ({deletions.length}):
        </p>
        {deletions.length === 0
          ? <p className="text-slate-500">None — all artifacts already absent or superseded.</p>
          : deletions.map((f, i) => (
            <p key={`${f.name}-${f.channel || i}`} className="font-mono text-[11px] text-slate-700">
              {f.name} · {fmtBytes(f.size)} · sha256 {f.sha256.slice(0, 12)}…
              {f.channel ? ` · ${f.channel}` : ''}
            </p>
          ))}
      </div>
      {skipped.length > 0 && (
        <div>
          <p className="font-semibold text-slate-700 mb-1">Skipped (will NOT be deleted):</p>
          {skipped.map((f, i) => (
            <p key={`${f.name}-skip-${i}`} className="font-mono text-[11px] text-slate-500">{f.name} — {f.reason}</p>
          ))}
        </div>
      )}
      <div className="rounded bg-slate-50 border p-2">
        <p className="font-semibold text-slate-700 mb-1">Non-PHI evidence that WILL REMAIN after purge:</p>
        <ul className="list-disc list-inside text-slate-600 space-y-0.5">
          <li>The staging run record (artifact names, hashes, counts, timestamps, operators)</li>
          <li>Hash-chained sensitive-audit events (staged + purged)</li>
          <li>PHI access log entries</li>
        </ul>
      </div>
      <p className="text-[11px] text-slate-500">
        Deletion is a filesystem removal on the managed container disk — not cryptographic or
        physical erasure; block-level remanence is governed by the hosting platform.
      </p>
    </div>
  );
};

const PurgeResult = ({ result }) => (
  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-1.5 text-xs"
       data-testid="cert-staging-purge-result">
    <p className="font-bold text-emerald-900 flex items-center gap-1.5">
      <CheckCircle2 className="h-4 w-4" /> PURGE PASS — run is now PURGED
    </p>
    <p className="text-emerald-900">
      PHI-bearing artifacts before: <strong>{result.before_count}</strong> · after:{' '}
      <strong>{result.before_count - result.deletion.deleted.length - (result.deletion.gridfs?.deleted?.length || 0)}</strong>{' '}
      (disk deleted {result.deletion.deleted.length}, encrypted-store deleted{' '}
      {result.deletion.gridfs?.deleted?.length || 0}, skipped {result.deletion.skipped.length})
    </p>
    {result.deletion.gridfs && (
      <p className="text-emerald-900">
        Encrypted store: {result.deletion.gridfs.verified_absent
          ? 'file + chunks verified ABSENT from the live database'
          : `deletion NOT fully verified (${result.deletion.gridfs.remaining_files} files, ${result.deletion.gridfs.remaining_chunks} chunks remain)`}
      </p>
    )}
    <p className="text-emerald-900">
      Post-purge verification: {result.deletion.post_purge_verified
        ? 'server confirmed every deleted artifact is gone from the staging area'
        : 'NOT VERIFIED'}
    </p>
    <p className="text-emerald-900">Non-PHI audit evidence (run record, hashes, audit events) was preserved.</p>
    <p className="text-[11px] text-emerald-800/80">{result.deletion.semantics}</p>
  </div>
);

export const CertPhiStagingPanel = () => {
  const [retention, setRetention] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [preview, setPreview] = useState(null);
  const [phrase, setPhrase] = useState('');
  const [purgeError, setPurgeError] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [retryAction, setRetryAction] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get('/cert-staging/retention'),
        api.get('/cert-staging/runs'),
      ]);
      setRetention(r1.data);
      setRuns(r2.data.runs || []);
    } catch (err) {
      if (err.response?.status === 403) setForbidden(true);
      else toast.error('Failed to load CERT staging status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPurge = useCallback(async (run) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cert-staging/runs/${run.id}/purge-preview`);
      setSelectedRun(run);
      setPreview(data);
      setPhrase('');
      setPurgeError('');
      setResult(null);
      setDialogOpen(true);
    } catch (err) {
      if (isStepUp(err)) {
        setRetryAction(() => () => openPurge(run));
        setStepUpOpen(true);
      } else {
        const d = err.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Purge preview refused');
      }
    } finally {
      setBusy(false);
    }
  }, []);

  const confirmPurge = useCallback(async () => {
    if (!selectedRun) return;
    setBusy(true);
    setPurgeError('');
    try {
      const beforeCount = (preview?.plan || []).filter((p) => p.action === 'delete').length;
      const { data } = await api.post(`/cert-staging/runs/${selectedRun.id}/purge`, {
        confirm_phrase: phrase,
      });
      setResult({ ...data, before_count: beforeCount });
      toast.success('CERT staging run purged — non-PHI evidence preserved');
      await load();
    } catch (err) {
      if (isStepUp(err)) {
        setRetryAction(() => () => confirmPurge());
        setStepUpOpen(true);
      } else {
        const d = err.response?.data?.detail;
        setPurgeError(typeof d === 'string' ? d : 'Purge refused by the server.');
      }
    } finally {
      setBusy(false);
    }
  }, [selectedRun, phrase, preview, load]);

  if (forbidden) return null;
  const requiredPhrase = preview?.confirm_phrase_required || '';
  const phraseOk = phrase.trim() === requiredPhrase;

  return (
    <Card className="p-5 space-y-4" data-testid="cert-staging-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Archive className="h-5 w-5 text-slate-700" />
          <h3 className="font-semibold text-slate-900">CERT PHI Staging &amp; Retention</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="cert-staging-refresh-btn">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-xs text-slate-600 -mt-2">
        The isolated staging area is the ONLY place CERT member data may exist — never in patient,
        claims or EVV records, never in logs, never in version control. Every run below is tracked
        through its full lifecycle: STAGED → CERT IN PROGRESS → CERT COMPLETE → RETENTION PENDING → PURGED.
      </p>

      <RetentionCard retention={retention} onApprove={() => setApproveOpen(true)} />

      {loading ? (
        <p className="text-sm text-slate-500">Loading staging runs…</p>
      ) : runs.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="cert-staging-no-runs">
          No CERT staging runs recorded yet. Runs appear here after each certification batch generation.
        </p>
      ) : (
        <div className="overflow-x-auto border rounded-md">
          <Table data-testid="cert-staging-runs-table">
            <TableHeader>
              <TableRow className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <TableHead className="px-3 py-2 font-semibold h-auto">Run</TableHead>
                <TableHead className="px-3 py-2 font-semibold h-auto">Created (staged)</TableHead>
                <TableHead className="px-3 py-2 font-semibold h-auto">Operator</TableHead>
                <TableHead className="px-3 py-2 font-semibold h-auto">Dataset</TableHead>
                <TableHead className="px-3 py-2 font-semibold h-auto">Source agency</TableHead>
                <TableHead className="px-3 py-2 font-semibold h-auto text-center">Inquiries</TableHead>
                <TableHead className="px-3 py-2 font-semibold h-auto">Lifecycle</TableHead>
                <TableHead className="px-3 py-2 font-semibold h-auto">Purge due</TableHead>
                <TableHead className="px-3 py-2 h-auto" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => renderRunRow(run, retention, openPurge))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!busy) setDialogOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="cert-staging-purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" /> Purge CERT staging run
            </DialogTitle>
            <DialogDescription className="font-mono text-[11px]">
              run {selectedRun?.id} · fingerprint {selectedRun?.dataset_fingerprint?.slice(0, 16)}…
              {selectedRun?.source_agency_name
                ? ` · source agency ${selectedRun.source_agency_name}` : ''}
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <PurgeResult result={result} />
          ) : (
            <>
              <PurgePlan preview={preview} />
              <div className="space-y-2">
                <p className="text-xs text-slate-600">
                  This purges exactly ONE run — no wildcard or bulk deletion exists. To proceed,
                  type exactly:{' '}
                  <span className="font-mono font-semibold text-red-700 break-all">{requiredPhrase}</span>
                </p>
                <Input value={phrase} onChange={(e) => setPhrase(e.target.value)}
                       placeholder="PURGE CERT PHI <run-id>"
                       className="font-mono text-xs"
                       data-testid="cert-staging-purge-phrase-input" />
                {purgeError && (
                  <p className="text-xs text-red-600 flex items-start gap-1" data-testid="cert-staging-purge-error">
                    <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {purgeError}
                  </p>
                )}
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy}
                    data-testid="cert-staging-purge-close-btn">
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button onClick={confirmPurge} disabled={busy || !phraseOk}
                      className="bg-red-700 hover:bg-red-800"
                      data-testid="cert-staging-purge-confirm-btn">
                PURGE THIS RUN
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MfaStepUpDialog
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        onVerified={() => { if (retryAction) retryAction(); }}
      />

      <ApprovePolicyDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        onApproved={load}
        onStepUp={(retry) => { setRetryAction(() => retry); setStepUpOpen(true); }}
      />
    </Card>
  );
};

export default CertPhiStagingPanel;
